import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithCustomToken,
  linkWithCredential,
  updatePassword,
  type User as FirebaseUser,
  type OAuthCredential,
} from 'firebase/auth'
import { httpsCallable } from 'firebase/functions'
import { doc, getDoc, setDoc, updateDoc, serverTimestamp, onSnapshot } from '@/lib/firestore'
import { auth, db, functions } from '@/config/firebase'
import type { User, Tenant, Language, Theme, NotificationPreferences } from '@/types'
import type { AuthState, ModuleSettings } from './state'
import { DEFAULT_MODULE_SETTINGS } from './state'
import { resetAllDataStores } from '@/stores/resetStores'
import { isDesktop, signInWithGoogleDesktop } from '@/lib/desktop'
import { setTelemetryContext, clearTelemetryContext, setTokenProvider, initErrorCapture } from '@/services/telemetry'

export interface LoginCredentials {
  email: string
  password: string
}

export interface RegisterData {
  email: string
  password: string
  displayName: string
  companyName?: string
}

export const createActions = (
  state: AuthState,
  setUser: (user: User | null) => void,
  setLoading: (loading: boolean) => void,
  setError: (error: string | null) => void,
  setInitialized: (initialized: boolean) => void,
  setCurrentTenant: (tenant: Tenant | null) => void,
  setImpersonatingTenantId: (tenantId: string | null) => void,
  setModuleSettings: (settings: ModuleSettings) => void,
) => {
  // Realtime listener on user doc to detect deactivation
  let unsubscribeUserSnapshot: (() => void) | null = null

  const startUserRealtimeListener = (uid: string) => {
    // Clean up previous listener
    if (unsubscribeUserSnapshot) {
      unsubscribeUserSnapshot()
      unsubscribeUserSnapshot = null
    }

    unsubscribeUserSnapshot = onSnapshot(doc(db, 'users', uid), (snapshot) => {
      if (!snapshot.exists()) return
      const data = snapshot.data()
      if (data.isActive === false) {
        // User was deactivated — force logout
        // eslint-disable-next-line no-console
        console.warn('User deactivated, forcing logout')
        logout()
      }
    })
  }

  const stopUserRealtimeListener = () => {
    if (unsubscribeUserSnapshot) {
      unsubscribeUserSnapshot()
      unsubscribeUserSnapshot = null
    }
  }

  const fetchUserData = async (firebaseUser: FirebaseUser): Promise<User | null> => {
    try {
      const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid))
      if (userDoc.exists()) {
        const userData = userDoc.data()
        return {
          id: firebaseUser.uid,
          tenantId: userData.tenantId,
          email: userData.email,
          displayName: userData.displayName,
          role: userData.role,
          organizationId: userData.organizationId,
          isOrganizationOwner: userData.isOrganizationOwner,
          assignedOrganizationIds: userData.assignedOrganizationIds || [],
          tenantIds: userData.tenantIds || [],
          tenantRoles: userData.tenantRoles || {},
          mustChangePassword: userData.mustChangePassword === true,
          avatarUrl: userData.avatarUrl,
          phone: userData.phone,
          language: userData.language || 'es',
          theme: userData.theme || 'light',
          notificationPreferences: userData.notificationPreferences || undefined,
          telegramChatId: userData.telegramChatId || undefined,
          telegramUserId: userData.telegramUserId || undefined,
          telegramLinkedAt: userData.telegramLinkedAt?.toDate() || undefined,
          telegramBotUsername: userData.telegramBotUsername || undefined,
          isActive: userData.isActive,
          createdAt: userData.createdAt?.toDate() || new Date(),
          updatedAt: userData.updatedAt?.toDate() || new Date(),
          createdBy: userData.createdBy,
          updatedBy: userData.updatedBy,
        }
      }
      return null
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching user data:', error)
      return null
    }
  }

  const fetchTenantData = async (tenantId: string): Promise<Tenant | null> => {
    try {
      const tenantDoc = await getDoc(doc(db, 'tenants', tenantId))
      if (tenantDoc.exists()) {
        const data = tenantDoc.data()
        return {
          id: tenantDoc.id,
          name: data.name,
          slug: data.slug,
          status: data.status,
          plan: data.plan,
          branding: data.branding || { companyName: data.name },
          limits: data.limits || { maxUsers: 10, maxProjects: 5, maxStorageMB: 1024 },
          ownerId: data.ownerId,
          domain: data.domain,
          features: data.features || [],
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          createdBy: data.createdBy,
          updatedBy: data.updatedBy,
        } as Tenant
      }
      return null
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching tenant data:', error)
      return null
    }
  }

  const fetchModuleSettings = async (tenantId: string): Promise<void> => {
    try {
      const modulesDoc = await getDoc(doc(db, `tenants/${tenantId}/settings/modules`))
      if (modulesDoc.exists()) {
        setModuleSettings({ ...DEFAULT_MODULE_SETTINGS, ...modulesDoc.data() } as ModuleSettings)
      } else {
        setModuleSettings({ ...DEFAULT_MODULE_SETTINGS })
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching module settings:', error)
      setModuleSettings({ ...DEFAULT_MODULE_SETTINGS })
    }
  }

  const initializeAuth = (): Promise<void> => {
    return new Promise((resolve) => {
      const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        setLoading(true)
        try {
          if (firebaseUser) {
            const userData = await fetchUserData(firebaseUser)
            if (userData && userData.isActive) {
              setUser(userData)
              // Initialize telemetry with user context
              setTokenProvider(() => firebaseUser.getIdToken())
              setTelemetryContext(userData.tenantId || 'unknown', userData.id)
              initErrorCapture()
              // Start realtime listener for deactivation
              startUserRealtimeListener(firebaseUser.uid)
              // Multi-tenant (Modelo C): restaurar el tenant activo persistido si es válido
              // para este user, ANTES de que los stores consulten (switch limpio vía reload).
              let activeTenantId = userData.tenantId
              try {
                const saved = localStorage.getItem('activeTenantId')
                if (saved && (saved === userData.tenantId || userData.tenantIds?.includes(saved))) {
                  activeTenantId = saved
                  if (saved !== userData.tenantId) setImpersonatingTenantId(saved)
                } else if (saved) {
                  localStorage.removeItem('activeTenantId') // ya no es válido para este user
                }
              } catch { /* localStorage no disponible */ }
              // Load tenant data for the active tenant
              if (activeTenantId) {
                const tenant = await fetchTenantData(activeTenantId)
                setCurrentTenant(tenant)
                await fetchModuleSettings(activeTenantId)
              } else {
                setCurrentTenant(null)
              }
            } else {
              stopUserRealtimeListener()
              await signOut(auth)
              setUser(null)
              setCurrentTenant(null)
              setError('Usuario inactivo o no encontrado')
            }
          } else {
            stopUserRealtimeListener()
            setUser(null)
            setCurrentTenant(null)
            setImpersonatingTenantId(null)
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Auth initialization error:', error)
          setUser(null)
          setCurrentTenant(null)
        } finally {
          setLoading(false)
          setInitialized(true)
          resolve()
        }
      })

      // Return unsubscribe function for cleanup
      return unsubscribe
    })
  }

  const login = async (credentials: LoginCredentials): Promise<void> => {
    setLoading(true)
    setError(null)
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        credentials.email,
        credentials.password
      )
      const userData = await fetchUserData(userCredential.user)
      if (userData && userData.isActive) {
        setUser(userData)
      } else {
        await signOut(auth)
        throw new Error('Usuario inactivo o no encontrado')
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Error al iniciar sesion'
      setError(errorMessage)
      throw error
    } finally {
      setLoading(false)
    }
  }

  /**
   * Helper: complete login after obtaining the correct Firebase Auth session.
   * Fetches user data from Firestore, loads tenant, and sets store state.
   */
  const completeGoogleLogin = async (firebaseUser: FirebaseUser): Promise<void> => {
    const userData = await fetchUserData(firebaseUser)
    if (!userData || !userData.isActive) {
      await signOut(auth)
      throw new Error('Usuario inactivo o no encontrado')
    }
    setUser(userData)
    if (userData.tenantId) {
      const tenant = await fetchTenantData(userData.tenantId)
      setCurrentTenant(tenant)
      await fetchModuleSettings(userData.tenantId)
    }
  }

  /**
   * Helper: call the googleSignIn CF to get a custom token for an existing user,
   * sign in with that token, and optionally link the Google credential.
   */
  const resolveGoogleViaCloudFunction = async (
    cfPayload: Record<string, unknown>,
    credentialToLink?: OAuthCredential | null,
  ): Promise<void> => {
    const googleSignInFn = httpsCallable(functions, 'googleSignIn')
    const result = await googleSignInFn(cfPayload)
    const data = result.data as { customToken?: string; alreadyLinked?: boolean }

    if (data.alreadyLinked) {
      // UID already matched — just finish login
      const currentUser = auth.currentUser
      if (currentUser) {
        await completeGoogleLogin(currentUser)
        return
      }
    }

    if (!data.customToken) {
      throw new Error('No tienes una cuenta registrada. Contacta a tu administrador.')
    }

    // Sign out of the orphan Google session (if any) and sign in as the existing user
    await signOut(auth)
    const customTokenResult = await signInWithCustomToken(auth, data.customToken)

    // Link Google credential so future sign-ins use the correct UID directly
    if (credentialToLink) {
      try {
        await linkWithCredential(customTokenResult.user, credentialToLink)
      } catch {
        // Non-critical: linking might fail if already linked or provider conflict.
        // The sign-in itself already succeeded.
      }
    }

    await completeGoogleLogin(customTokenResult.user)
  }

  const NO_ACCOUNT_MSG = 'Esta cuenta de Google no tiene acceso. Contacta a tu administrador.'

  const loginWithGoogle = async (): Promise<void> => {
    setLoading(true)
    setError(null)
    try {
      const provider = new GoogleAuthProvider()
      let userCredential

      try {
        // Desktop (Tauri) can't use signInWithPopup — Google blocks OAuth in
        // embedded webviews. Use the system-browser + loopback flow instead.
        // In the browser isDesktop() is false, so the web flow is unchanged.
        userCredential = isDesktop()
          ? await signInWithGoogleDesktop()
          : await signInWithPopup(auth, provider)
      } catch (popupError: unknown) {
        const fbError = popupError as { code?: string }

        // User closed the popup — silent return
        if (
          fbError.code === 'auth/popup-closed-by-user' ||
          fbError.code === 'auth/cancelled-popup-request'
        ) {
          return
        }

        // Account exists with different provider (email/password).
        // Use CF with the raw Google ID token to get a custom token.
        if (fbError.code === 'auth/account-exists-with-different-credential') {
          const pendingCredential = GoogleAuthProvider.credentialFromError(
            popupError as Parameters<typeof GoogleAuthProvider.credentialFromError>[0],
          ) as OAuthCredential | null

          if (pendingCredential?.idToken) {
            await resolveGoogleViaCloudFunction(
              { googleIdToken: pendingCredential.idToken },
              pendingCredential,
            )
            return
          }

          // Could not extract credential — fallback message
          throw new Error(
            'Tu email ya esta registrado con contraseña. ' +
            'Inicia sesion con tu email y contraseña.',
          )
        }

        throw popupError
      }

      // signInWithPopup succeeded — check if user exists by UID
      const userData = await fetchUserData(userCredential.user)

      if (userData) {
        // User found by UID (auto-linked or same account)
        if (!userData.isActive) {
          await signOut(auth)
          throw new Error('Usuario inactivo')
        }
        setUser(userData)
        if (userData.tenantId) {
          const tenant = await fetchTenantData(userData.tenantId)
          setCurrentTenant(tenant)
          await fetchModuleSettings(userData.tenantId)
        }
        return
      }

      // User NOT found by UID — Google created a new Auth account.
      // Call CF (authenticated) to find user by email and get custom token.
      // Pass idToken from Google credential as fallback if auth context is lost.
      const googleCredential = GoogleAuthProvider.credentialFromResult(
        userCredential,
      ) as OAuthCredential | null

      const cfPayload: Record<string, unknown> = {}
      if (googleCredential?.idToken) {
        cfPayload.googleIdToken = googleCredential.idToken
      }

      try {
        await resolveGoogleViaCloudFunction(cfPayload, googleCredential)
      } catch {
        // CF failed (no account, inactive, etc.) — clean up orphan session
        await signOut(auth)
        throw new Error(NO_ACCOUNT_MSG)
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error
        ? error.message
        : 'Error al iniciar sesion con Google'
      setError(errorMessage)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const register = async (data: RegisterData): Promise<void> => {
    setLoading(true)
    setError(null)
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        data.email,
        data.password
      )

      // Create user document
      const newUser: Omit<User, 'id'> = {
        email: data.email,
        displayName: data.displayName,
        role: 'client',
        language: 'es',
        theme: 'light',
        isActive: true,
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: userCredential.user.uid,
        updatedBy: userCredential.user.uid,
      }

      await setDoc(doc(db, 'users', userCredential.user.uid), {
        ...newUser,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })

      // If company name provided, create organization
      if (data.companyName) {
        const orgRef = doc(db, 'organizations', crypto.randomUUID())
        await setDoc(orgRef, {
          name: data.companyName,
          ownerId: userCredential.user.uid,
          memberIds: [userCredential.user.uid],
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          createdBy: userCredential.user.uid,
          updatedBy: userCredential.user.uid,
        })

        // Update user with organization
        await updateDoc(doc(db, 'users', userCredential.user.uid), {
          organizationId: orgRef.id,
          isOrganizationOwner: true,
          updatedAt: serverTimestamp(),
        })

        newUser.organizationId = orgRef.id
        newUser.isOrganizationOwner = true
      }

      setUser({
        id: userCredential.user.uid,
        ...newUser,
      })
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Error al registrar usuario'
      setError(errorMessage)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const logout = async (): Promise<void> => {
    setLoading(true)
    try {
      // Stop realtime listener
      stopUserRealtimeListener()

      // Clear telemetry context
      clearTelemetryContext()

      // SECURITY: Reset all data stores BEFORE signing out
      // This prevents data leakage between users/tenants
      resetAllDataStores()

      await signOut(auth)
      setUser(null)
      setCurrentTenant(null)
      setImpersonatingTenantId(null)
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Error al cerrar sesion'
      setError(errorMessage)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const resetPassword = async (email: string): Promise<void> => {
    setLoading(true)
    setError(null)
    try {
      const sendPasswordResetEmailFn = httpsCallable(functions, 'sendPasswordResetEmailFn')
      await sendPasswordResetEmailFn({ email })
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Error al enviar correo de recuperacion'
      setError(errorMessage)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const updateUserPreferences = async (preferences: { language?: Language; theme?: Theme; notificationPreferences?: NotificationPreferences }): Promise<void> => {
    if (!state.user) return

    try {
      await updateDoc(doc(db, 'users', state.user.id), {
        ...preferences,
        updatedAt: serverTimestamp(),
        updatedBy: state.user.id,
      })

      setUser({
        ...state.user,
        ...preferences,
        updatedAt: new Date(),
      })
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Error al actualizar preferencias'
      setError(errorMessage)
      throw error
    }
  }

  const updateUserProfile = async (profile: { displayName?: string; phone?: string; telegramChatId?: string }): Promise<void> => {
    if (!state.user) return

    try {
      await updateDoc(doc(db, 'users', state.user.id), {
        ...profile,
        updatedAt: serverTimestamp(),
        updatedBy: state.user.id,
      })

      setUser({
        ...state.user,
        ...profile,
        updatedAt: new Date(),
      })
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Error al actualizar perfil'
      setError(errorMessage)
      throw error
    }
  }

  const clearError = (): void => {
    setError(null)
  }

  const startImpersonation = async (tenantId: string): Promise<void> => {
    // SECURITY: Reset all data stores before switching tenants
    // This prevents data from previous tenant being visible
    resetAllDataStores()

    const tenant = await fetchTenantData(tenantId)
    if (tenant) {
      setImpersonatingTenantId(tenantId)
      setCurrentTenant(tenant)
      await fetchModuleSettings(tenantId)
    }
  }

  const stopImpersonation = async (): Promise<void> => {
    // SECURITY: Reset all data stores before switching back
    resetAllDataStores()

    try { localStorage.removeItem('activeTenantId') } catch { /* noop */ }
    setImpersonatingTenantId(null)
    // Restore user's own tenant data
    const userTenantId = state.user?.tenantId
    if (userTenantId) {
      const tenant = await fetchTenantData(userTenantId)
      setCurrentTenant(tenant)
      await fetchModuleSettings(userTenantId)
    } else {
      setCurrentTenant(null)
    }
  }

  // Multi-tenant (Modelo C): tenants entre los que el usuario puede cambiar (allowed-list).
  // Devuelve los docs de Tenant de su `tenantIds`; vacío si es single-tenant.
  const fetchSelectableTenants = async (): Promise<Tenant[]> => {
    const ids = state.user?.tenantIds ?? []
    if (ids.length <= 1) return []
    const tenants = await Promise.all(ids.map((id) => fetchTenantData(id)))
    return tenants.filter((t): t is Tenant => t !== null)
  }

  // Cambia el tenant activo del usuario multi-tenant. Persiste la elección y navega al DASHBOARD
  // con carga completa → re-init limpio (initializeAuth restaura el tenant activo antes de que los
  // stores consulten) y además saca al usuario de cualquier URL con data del tenant anterior
  // (ej. /projects/<id>), evitando ver un detalle que ya no corresponde al tenant activo.
  const selectTenant = async (tenantId: string): Promise<void> => {
    try {
      if (tenantId === state.user?.tenantId) localStorage.removeItem('activeTenantId')
      else localStorage.setItem('activeTenantId', tenantId)
    } catch { /* localStorage no disponible */ }
    window.location.assign('/dashboard')
  }

  // Cambio FORZADO de la clave temporal (mustChangePassword). El usuario acaba de loguear con la
  // temporal → updatePassword funciona (recent login); luego limpiamos el flag (self-update
  // permitido por las rules: mustChangePassword no está en la denylist de campos sensibles).
  const completeForcedPasswordChange = async (newPassword: string): Promise<void> => {
    const current = auth.currentUser
    if (!current) throw new Error('No hay sesión activa')
    await updatePassword(current, newPassword)
    await updateDoc(doc(db, 'users', current.uid), {
      mustChangePassword: false,
      updatedAt: serverTimestamp(),
      updatedBy: current.uid,
    })
    if (state.user) setUser({ ...state.user, mustChangePassword: false })
  }

  return {
    initializeAuth,
    login,
    loginWithGoogle,
    register,
    logout,
    resetPassword,
    updateUserPreferences,
    updateUserProfile,
    clearError,
    startImpersonation,
    stopImpersonation,
    fetchSelectableTenants,
    selectTenant,
    completeForcedPasswordChange,
  }
}
